// Polls one generation job. Never creates a job. Follows the SAME job until the backend returns a
// terminal state (done/failed) or 404. Cadence tiers: fast (~118s) -> medium (5s) -> long (20s).
// No client abandonment shorter than the server's 6000s active-job TTL; the job doc's TTL 404s first.
export function createJobPoll({
  getJob, onProgress, onDone, onFailed, onExpired, onGiveUp,
  setTimer = setTimeout, clearTimer = clearTimeout,
  fastFirstMs = 900, fastMs = 1300, fastMax = 90,      // ~118s fast phase (unchanged cadence)
  mediumMs = 5000, mediumUntilMs = 600000,             // ~5s cadence until ~10 min elapsed
  longMs = 20000,                                      // ~15-30s cadence afterwards (load-friendly)
  maxTotalMs = 6600000,                                // safety net ONLY: > server 6000s active TTL (+margin)
}) {
  let handle = null, attempts = 0, elapsedMs = 0, stopped = false;
  const stop = () => { stopped = true; if (handle) clearTimer(handle); };
  const nextMs = () => (attempts < fastMax ? fastMs : (elapsedMs < mediumUntilMs ? mediumMs : longMs));
  const phaseOf = () => (attempts <= fastMax ? "fast" : (elapsedMs < mediumUntilMs ? "medium" : "long"));
  const schedule = (jobId) => { const ms = nextMs(); elapsedMs += ms; handle = setTimer(() => tick(jobId), ms); };
  async function tick(jobId) {
    if (stopped) return;
    attempts += 1;
    if (elapsedMs >= maxTotalMs) { onGiveUp(); return; }   // aligned above server TTL; normally unreachable (404 first)
    let res;
    try {
      res = await getJob(jobId);
    } catch (e) {
      if (e?.response?.status === 404) { onExpired(); return; }
      schedule(jobId); return;                              // transient error: keep following the healthy job
    }
    if (res.status === "done") { onDone(res); return; }
    if (res.status === "failed" || res.status === "busy") { onFailed(res); return; }
    onProgress({ status: res.status, position: res.position, phase: phaseOf() });
    schedule(jobId);
  }
  return { start: (jobId) => { elapsedMs += fastFirstMs; handle = setTimer(() => tick(jobId), fastFirstMs); }, stop };
}
